/**
 * ProjectContextTab — the brief every agent in the project receives.
 *
 * Managers edit; everyone else reads. The read-only view is a first-class
 * rendering rather than a disabled textarea, because for most members this is
 * documentation — "what are we assuming in here" — and a greyed-out form field
 * reads as broken.
 */

import { useEffect, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import type { ProjectSummary } from '../types';
import { EmptyBlock } from './StateBlocks';
import './ProjectTabs.css';

interface ProjectContextTabProps {
  project: ProjectSummary;
  onSave: (values: {
    project_description: string;
    project_context_prompt: string;
  }) => Promise<void>;
}

export function ProjectContextTab({ project, onSave }: ProjectContextTabProps) {
  const { t } = useLanguage();
  const [editing, setEditing] = useState(false);
  const [description, setDescription] = useState(project.project_description || '');
  const [contextPrompt, setContextPrompt] = useState(project.project_context_prompt || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed whenever the project changes underneath us (another manager saved,
  // or the page refetched) so the editor never holds a stale draft silently.
  useEffect(() => {
    setDescription(project.project_description || '');
    setContextPrompt(project.project_context_prompt || '');
  }, [project.project_description, project.project_context_prompt]);

  const canEdit = project.can_manage && !project.is_archived;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave({
        project_description: description.trim(),
        project_context_prompt: contextPrompt.trim(),
      });
      setEditing(false);
    } catch (err: any) {
      setError(err?.response?.data?.error
        || (t('projects.context.saveFailed') || 'Could not save the project brief.'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDescription(project.project_description || '');
    setContextPrompt(project.project_context_prompt || '');
    setError(null);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="pj-tab context-tab">
        {error && <div className="pj-form-error">{error}</div>}

        <div className="pj-field">
          <label className="pj-label" htmlFor="pj-context-description">
            {t('projects.form.descriptionLabel') || 'Description'}
          </label>
          <textarea dir="auto"
            id="pj-context-description"
            className="pj-textarea"
            style={{ minHeight: 64 }}
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder={t('projects.form.descriptionPlaceholder')
              || 'What this project is for, in a sentence.'}
          />
        </div>

        <div className="pj-field">
          <label className="pj-label" htmlFor="pj-context-prompt">
            {t('projects.form.contextLabel') || 'Project brief for the agents'}
          </label>
          <textarea dir="auto"
            id="pj-context-prompt"
            className="pj-textarea context-prompt-input"
            value={contextPrompt}
            onChange={e => setContextPrompt(e.target.value)}
            placeholder={t('projects.form.contextPlaceholder')
              || 'Always check the budget sheet before quoting a number. Answers should be short and practical.'}
          />
          <p className="pj-hint">
            {t('projects.context.hint')
              || 'Applied to every conversation in this project, alongside the project files. Changes affect new messages immediately.'}
          </p>
        </div>

        <div className="context-actions">
          <button type="button" className="pj-btn pj-btn-secondary" onClick={handleCancel} disabled={saving}>
            {t('common.cancel') || 'Cancel'}
          </button>
          <button type="button" className="pj-btn pj-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? (t('common.saving') || 'Saving…') : (t('common.save') || 'Save')}
          </button>
        </div>
      </div>
    );
  }

  const hasContent = !!(project.project_description || project.project_context_prompt);

  if (!hasContent) {
    return (
      <div className="pj-tab context-tab">
        <EmptyBlock
          icon={(
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" />
              <path d="M14 3v5h5M9 13h6M9 17h4" />
            </svg>
          )}
          title={t('projects.context.emptyTitle') || 'No brief yet'}
          body={canEdit
            ? (t('projects.context.emptyBodyManager')
              || 'Tell the agents what this project is and how to behave in it. Every conversation here starts with what you write.')
            : (t('projects.context.emptyBodyMember')
              || 'A project manager has not written a brief for the agents yet.')}
          action={canEdit ? (
            <button type="button" className="pj-btn pj-btn-primary" onClick={() => setEditing(true)}>
              {t('projects.context.write') || 'Write the brief'}
            </button>
          ) : undefined}
        />
      </div>
    );
  }

  return (
    <div className="pj-tab context-tab">
      <div className="context-head">
        <div>
          <h3 className="context-heading">{t('projects.context.heading') || 'Project brief'}</h3>
          <p className="pj-hint">
            {t('projects.context.hint')
              || 'Applied to every conversation in this project, alongside the project files.'}
          </p>
        </div>
        {canEdit && (
          <button type="button" className="pj-btn pj-btn-secondary pj-btn-sm" onClick={() => setEditing(true)}>
            {t('common.edit') || 'Edit'}
          </button>
        )}
      </div>

      {project.project_description && (
        <section className="context-block">
          <h4 className="context-block-title">
            {t('projects.form.descriptionLabel') || 'Description'}
          </h4>
          <p className="context-block-text" dir="auto">{project.project_description}</p>
        </section>
      )}

      {project.project_context_prompt && (
        <section className="context-block">
          <h4 className="context-block-title">
            {t('projects.context.promptTitle') || 'Instructions to the agents'}
          </h4>
          <p className="context-block-text context-block-prompt" dir="auto">
            {project.project_context_prompt}
          </p>
        </section>
      )}
    </div>
  );
}

export default ProjectContextTab;
